import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Table, Card, Select, DatePicker, Button, Tag, Space, Input, Image } from 'antd';
import { SoundOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getAllAudios } from '../../graphql/queries';
import './Audios.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

const STATUS_COLORS = {
  SUCCEEDED: 'success',
  FAILED: 'error',
  RUNNING: 'processing',
  IN_QUE: 'warning',
  PENDING: 'default',
  THROTTLED: 'purple'
};

const SOURCE_COLORS = {
  FAL_AI: 'blue',
  KIE_AI: 'green'
};

const Audios = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [status, setStatus] = useState(searchParams.get('status') || null);
  const [generationSource, setGenerationSource] = useState(searchParams.get('source') || null);
  const [rcUserId, setRcUserId] = useState(searchParams.get('rcUserId') || '');
  const [dateRange, setDateRange] = useState([null, null]);

  const { loading, data, refetch } = useQuery(getAllAudios, {
    variables: {
      page,
      pageSize,
      status: status || undefined,
      generationSource: generationSource || undefined,
      rcUserId: rcUserId || undefined,
      startDate: dateRange[0] ? dateRange[0].toISOString() : undefined,
      endDate: dateRange[1] ? dateRange[1].toISOString() : undefined,
    },
    fetchPolicy: 'network-only',
  });

  const handleTableChange = (pagination) => {
    setPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleReset = () => {
    setStatus(null);
    setGenerationSource(null);
    setRcUserId('');
    setDateRange([null, null]);
    setPage(1);
    setSearchParams({});
  };

  const handleSearch = () => {
    const params = {};
    if (status) params.status = status;
    if (generationSource) params.source = generationSource;
    if (rcUserId) params.rcUserId = rcUserId;
    setSearchParams(params);
    refetch();
  };

  const columns = [
    {
      title: 'Image',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      width: 80,
      render: (imageUrl) => (
        imageUrl ? (
          <Image
            src={imageUrl}
            alt="Cover"
            width={60}
            height={60}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
          />
        ) : (
          <div className="audio-placeholder">
            <SoundOutlined />
          </div>
        )
      )
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      ellipsis: true,
      render: (title, record) => (
        <span className="audio-title" onClick={() => navigate(`/audio/${record.id}`)}>
          {title || 'Untitled'}
        </span>
      )
    },
    {
      title: 'Prompt',
      dataIndex: 'prompt',
      key: 'prompt',
      ellipsis: true,
      render: (prompt) => prompt || '-'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={STATUS_COLORS[status] || 'default'}>{status}</Tag>
      )
    },
    {
      title: 'Source',
      dataIndex: 'generationSource',
      key: 'generationSource',
      width: 100,
      render: (source) => (
        <Tag color={SOURCE_COLORS[source] || 'default'}>{source}</Tag>
      )
    },
    {
      title: 'Progress',
      dataIndex: 'progressRatio',
      key: 'progressRatio',
      width: 80,
      render: (progress) => progress !== null ? `${progress}%` : '-'
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm')
    },
  ];

  return (
    <div className="audios-container">
      {/* Page Header */}
      <div className="audios-header">
        <p className="audios-breadcrumb">Pages / Audios</p>
        <h1 className="audios-title">Audios</h1>
      </div>

      <Card className="audios-card">
        <div className="filters-container">
          <Space wrap>
            <Select
              placeholder="Status"
              allowClear
              style={{ width: 140 }}
              value={status}
              onChange={setStatus}
            >
              <Option value="SUCCEEDED">Succeeded</Option>
              <Option value="FAILED">Failed</Option>
              <Option value="RUNNING">Running</Option>
              <Option value="IN_QUE">In Queue</Option>
              <Option value="PENDING">Pending</Option>
              <Option value="THROTTLED">Throttled</Option>
            </Select>

            <Select
              placeholder="Source"
              allowClear
              style={{ width: 120 }}
              value={generationSource}
              onChange={setGenerationSource}
            >
              <Option value="FAL_AI">FAL_AI</Option>
              <Option value="KIE_AI">KIE_AI</Option>
            </Select>

            <Input
              placeholder="User ID"
              style={{ width: 200 }}
              value={rcUserId}
              onChange={(e) => setRcUserId(e.target.value)}
              prefix={<SearchOutlined />}
            />

            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              format="YYYY-MM-DD"
            />

            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
              Search
            </Button>

            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              Reset
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={data?.getAllAudios?.audios || []}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize: pageSize,
            total: data?.getAllAudios?.totalCount || 0,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} audios`,
          }}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => navigate(`/audio/${record.id}`),
            style: { cursor: 'pointer' }
          })}
          scroll={{ x: 1200 }}
        />
      </Card>
    </div>
  );
};

export default Audios;
